// Bristle-group brush dynamics and paint exchange.
//
// A brush is a set of bristle groups (each standing for many hairs), rooted
// in the ferrule at rest offsets from BrushGeometry. While painting:
//   - pressure splays the groups outward and brings outer groups into contact
//     (a round brush's mark widens with pressure and tapers as it lifts);
//   - each group's tip trails its root by a bend that grows with pressure and
//     bristle length and shrinks with stiffness, relaxing over a distance set
//     by stiffness (a spring-damper integrated over arc length, so the result
//     does not depend on how often pointer events arrive);
//   - tilt lays the brush over: the footprint stretches toward the tilt
//     direction and groups on the heel side lift off;
//   - every contacting tip exchanges material with the cells under it: it
//     picks up wet paint/water (limited by its free capacity) and releases its
//     own load. Each group has a small, fast-exchanging tip load fed slowly
//     from its belly (reservoir), as on a real brush where paint leaves from
//     the outside of the tip. Because pickup and release both move real
//     material, a red brush dragged through wet blue carries a blue streak
//     onward that fades over a few brush widths, while the belly is only
//     slowly contaminated;
//   - stiff bristles plough wet body paint aside, leaving grooves and ridges;
//   - on dry paper only tips with enough pressure or load reach the valleys of
//     the paper, so a lightly loaded brush skips over the tooth (dry brush);
//   - families differ in contact width (a rigger touches a thin line but its
//     long hairs trail at full length), in the patch each group touches
//     (separate fan streaks, coarse hog clumps, soft mop blending), in how
//     readily the ferrule turns with the stroke (flats keep their orientation)
//     and in intermittent contact (sponge pores, lifting hog clumps).

import { createBristleLayout } from './BrushGeometry.js';
import { createCarrier, loadCarrier, CAPACITY_HEADROOM } from '../physics/PaintDeposition.js';
import { exchangeStamp, LOAD_REACH, TIP_FRACTION } from './StampExchange.js';

// Cells per millisecond; about 2 m/s at 1 cell = 1 mm, faster than real painting.
const MAX_BRUSH_SPEED = 2;

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

// Deterministic smooth noise in [0, 1) along one axis (per bristle group).
function hash01(n) {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function valueNoise(seed, t) {
  const i = Math.floor(t);
  const f = t - i;
  const u = f * f * (3 - 2 * f);
  return hash01(seed * 7919 + i) * (1 - u) + hash01(seed * 7919 + i + 1) * u;
}
const smoothstep = (a, b, x) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

export function createBristleBrush(preset, options = {}) {
  const radius = Math.max(0.75, Number(options.radius) || 8);
  const groups = createBristleLayout(preset, options.seed || 1);
  const count = groups.length;
  // Enough load for a stroke ~LOAD_REACH radii long and the brush's width.
  const baseCapacity = (options.capacity ?? 1) * preset.capacity * LOAD_REACH * 2 * radius * radius * CAPACITY_HEADROOM / count;
  const brush = {
    preset,
    radius,
    groups,
    medium: options.medium,
    rotationMode: options.rotationMode || (preset.aspect > 1.2 ? 'direction' : 'direction'),
    fixedAngle: options.fixedAngle || 0,
    tooth: options.tooth ?? 0.6,
    carriers: groups.map((g) => createCarrier(baseCapacity * g.capacityScale, options.medium)),
    tipLoads: groups.map((g) => createCarrier(baseCapacity * g.capacityScale * TIP_FRACTION, options.medium)),
    tips: groups.map(() => [0, 0]),
    // Contact width (cells) and family mechanics (see BrushPresets).
    width: radius * (preset.widthScale ?? 1),
    footprintScale: preset.footprintScale ?? 1,
    rotationFollow: preset.rotationFollow ?? 1,
    angle: null,
    noiseSeed: ((options.seed || 1) * 131 + count) | 0,
    last: null,
    dir: [1, 0],
    travelled: 0,
    stats: { contacts: 0, deposited: 0, pickedUp: 0 },
  };
  brush.exchangeParams = stampExchangeParams(brush);
  if (options.mixture) reloadBrush(brush, options.medium, options.mixture, options.loading ?? 1);
  return brush;
}

export function reloadBrush(brush, medium, mixture, loading = 1) {
  brush.medium = medium;
  brush.exchangeParams = stampExchangeParams(brush);
  for (const carrier of brush.carriers) loadCarrier(carrier, medium, mixture, loading);
  for (const tip of brush.tipLoads) loadCarrier(tip, medium, mixture, loading);
}

export function brushLoadSummary(brush) {
  let water = 0;
  let body = 0;
  let capacity = 0;
  for (const c of [...brush.carriers, ...brush.tipLoads]) {
    water += c.water;
    body += c.body;
    capacity += c.capacity;
  }
  return { water, body, capacity, fullness: (water + body) / Math.max(1e-9, capacity) };
}

function pressureEffect(sample) {
  const p = Number.isFinite(sample.pressure) ? sample.pressure : 0.5;
  return clamp01(p);
}

// Begins a stroke at sample {x, y, pressure, tiltX, tiltY, twist, time}.
export function beginBristleStroke(brush, state, sample) {
  brush.last = { ...sample };
  brush.travelled = 0;
  brush.angle = null;
  for (const tip of brush.tips) {
    tip[0] = 0;
    tip[1] = 0;
  }
  applyStep(brush, state, sample, 0.5, brush.dir);
}

export function continueBristleStroke(brush, state, sample) {
  const last = brush.last;
  if (!last) {
    beginBristleStroke(brush, state, sample);
    return;
  }
  const dx = sample.x - last.x;
  const dy = sample.y - last.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 1e-3) {
    brush.last = { ...sample };
    return;
  }
  const step = Math.max(0.35, Math.min(1.0, brush.radius * 0.08));
  const steps = Math.max(1, Math.ceil(distance / step));
  const ds = distance / steps;
  const elapsed = Math.max(1, (sample.time ?? 0) - (last.time ?? 0));
  const speed = distance / elapsed; // cells per millisecond
  // Smoothed travel direction (blended per unit length so it is rate independent).
  const nx = dx / distance;
  const ny = dy / distance;
  const blend = 1 - Math.exp(-distance / Math.max(1, brush.radius * 0.5));
  let dirX = brush.dir[0] + (nx - brush.dir[0]) * blend;
  let dirY = brush.dir[1] + (ny - brush.dir[1]) * blend;
  const len = Math.hypot(dirX, dirY) || 1;
  dirX /= len;
  dirY /= len;
  brush.dir = [dirX, dirY];
  turnBrush(brush, nx, ny, distance);
  for (let s = 1; s <= steps; s += 1) {
    const t = s / steps;
    const p = {
      x: last.x + dx * t,
      y: last.y + dy * t,
      pressure: lerp(last.pressure, sample.pressure, t),
      tiltX: lerp(last.tiltX, sample.tiltX, t),
      tiltY: lerp(last.tiltY, sample.tiltY, t),
      twist: lerp(last.twist, sample.twist, t),
      speed,
    };
    applyStep(brush, state, p, ds, brush.dir, brush.travelled + distance * t);
  }
  brush.travelled += distance;
  brush.last = { ...sample };
}

function lerp(a, b, t) {
  const av = Number.isFinite(a) ? a : 0;
  const bv = Number.isFinite(b) ? b : av;
  return av + (bv - av) * t;
}

// Ferrule orientation in 'direction' mode. Round brushes have none to keep
// (rotationFollow 1: the travel direction). A flat keeps the orientation it
// starts the stroke with and turns only partly toward the travel direction,
// so moving along its edge paints a thin line; it is symmetric under a half
// turn, so it turns toward the nearer equivalent orientation.
function turnBrush(brush, nx, ny, distance) {
  if (brush.rotationFollow >= 1) return;
  const target = Math.atan2(ny, nx);
  if (brush.angle === null) {
    brush.angle = target;
    return;
  }
  let delta = target - brush.angle;
  delta -= Math.PI * Math.round(delta / Math.PI);
  brush.angle += delta * (1 - Math.exp(-(brush.rotationFollow * distance) / Math.max(1, brush.radius)));
}

// Fraction of contact a group keeps at arc length along (intermittent contact).
function contactKeep(brush, g, along, noise) {
  const n = valueNoise(brush.noiseSeed + g * 977, along / Math.max(0.5, noise.length * brush.radius));
  const cut = noise.amount * 0.55;
  return smoothstep(cut - 0.12, cut + 0.12, n);
}

function applyStep(brush, state, sample, ds, dir, along = 0) {
  const { preset, radius, groups, carriers, tips } = brush;
  const width = brush.width ?? radius;
  const pe = pressureEffect(sample);
  const angle = brush.rotationMode === 'pen' && Number.isFinite(sample.twist)
    ? (sample.twist * Math.PI) / 180
    : brush.rotationMode === 'fixed'
      ? brush.fixedAngle
      : brush.rotationFollow < 1 && brush.angle !== null
        ? brush.angle
        : Math.atan2(dir[1], dir[0]);
  const alongX = Math.cos(angle);
  const alongY = Math.sin(angle);
  const acrossX = -alongY;
  const acrossY = alongX;
  // Tilt (degrees from vertical, Pointer Events convention) as a vector of 0..1 magnitude.
  const tx = Math.max(-1, Math.min(1, (sample.tiltX || 0) / 70));
  const ty = Math.max(-1, Math.min(1, (sample.tiltY || 0) / 70));
  const tiltMag = Math.min(1, Math.hypot(tx, ty));
  const tiltDirX = tiltMag > 1e-3 ? tx / tiltMag : 0;
  const tiltDirY = tiltMag > 1e-3 ? ty / tiltMag : 0;

  const soft = 1 - preset.stiffness;
  const spread = 1 + preset.splay * pe * (0.5 + 0.5 * soft);
  const contactReach = 0.3 + 0.8 * Math.pow(pe, 0.7);
  const bendLength = radius * 0.6 * (preset.length || 0) * pe * (0.15 + soft);
  const relax = 1 - Math.exp(-ds / Math.max(0.3, radius * 0.25 * (0.2 + preset.stiffness)));
  const bristleRadius = Math.max(0.55, ((width * 1.7) / Math.sqrt(groups.length)) * (brush.footprintScale ?? 1)) * (0.75 + 0.5 * pe);
  // Fast strokes skip a little more over paper tooth. Speed is clamped to a
  // plausible hand speed: sparse or late pointer events (one big jump in a
  // millisecond) must not read as a supersonic brush that never touches down.
  const speed = Math.min(MAX_BRUSH_SPEED, sample.speed || 0);
  const speedSkip = 1 / (1 + 0.35 * speed * (1 - preset.stiffness * 0.5));
  const isSponge = preset.layout === 'sponge';
  const noise = preset.contactNoise || null;

  for (let g = 0; g < groups.length; g += 1) {
    const group = groups[g];
    // Outer bristles only touch down with enough pressure (round tapering).
    let edgeContact = preset.layout === 'flat' || preset.layout === 'filbert' || preset.layout === 'fan'
      ? 1
      : smoothstep(contactReach + 0.18, contactReach - 0.05, group.rest);
    let ox = (acrossX * group.u + alongX * group.v) * width * spread;
    let oy = (acrossY * group.u + alongY * group.v) * width * spread;
    if (isSponge) {
      // A sponge deforms and rolls as it is dragged: its pores wander, so
      // their marks do not line up into parallel tracks.
      const wander = Math.max(0.5, 0.6 * radius);
      ox += (valueNoise(brush.noiseSeed + g * 31 + 5, along / wander) - 0.5) * 0.5 * width;
      oy += (valueNoise(brush.noiseSeed + g * 31 + 9, along / wander) - 0.5) * 0.5 * width;
    }
    if (tiltMag > 0) {
      const heel = ox * tiltDirX + oy * tiltDirY;
      const stretch = heel * 1.1 * tiltMag;
      ox += tiltDirX * (stretch + width * 0.35 * tiltMag);
      oy += tiltDirY * (stretch + width * 0.35 * tiltMag);
      // Heel side lifts off when the brush is laid over.
      if (heel < 0) edgeContact *= 1 - smoothstep(0.2, 1.0, (-heel / width) * tiltMag * 1.5);
    }
    if (noise) edgeContact *= contactKeep(brush, g, along, noise);
    if (edgeContact <= 0.01) continue;
    const tip = tips[g];
    if (!isSponge) {
      const target = bendLength * group.lengthScale * (1 - group.stiffness * 0.6);
      tip[0] += (-dir[0] * target - tip[0]) * relax;
      tip[1] += (-dir[1] * target - tip[1]) * relax;
    }
    const stamp = {
      group: g,
      px: sample.x + ox + tip[0],
      py: sample.y + oy + tip[1],
      baseRadius: isSponge ? Math.max(0.6, group.poreRadius * width * 2) : bristleRadius,
      sponge: isSponge ? 1 : 0,
      edgeContact,
      pe,
      ds,
      speedSkip,
      acrossX,
      acrossY,
      dirX: dir[0],
      dirY: dir[1],
    };
    if (brush.sink) brush.sink(stamp);
    else brush.stats.contacts += exchangeStamp(state, brush.exchangeParams, carriers[g], brush.tipLoads[g], stamp);
  }
}

// Physical parameters the per-cell exchange needs (CPU reference and GPU kernel).
export function stampExchangeParams(brush) {
  const { preset, medium } = brush;
  return {
    radius: brush.radius,
    release: preset.release,
    stiffness: preset.stiffness,
    plough: preset.plough || 0,
    pickupScale: (preset.pickup ?? 0.5) * ((medium && medium.pickup) ?? 0.5),
    tooth: brush.tooth,
  };
}
